import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "../lib/auth";
import { MagnifyingGlass, User, SignOut, List, X, Sun, Moon } from "@phosphor-icons/react";
import { toast } from "sonner";

const LOGO = "/ticketup_logo.png";

const COUNTRIES = [
  { code: "BD", name: "Bangladesh", flag: "🇧🇩" },
  { code: "AU", name: "Australia", flag: "🇦🇺" },
  { code: "TH", name: "Thailand", flag: "🇹🇭" },
];

const SectionLabel = ({ children }) => (
  <p className="px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-neutral-400">{children}</p>
);

function useIsActive(to) {
  const location = useLocation();
  const toUrl = new URL(to, "http://localhost");
  const locUrl = new URL(location.pathname + location.search, "http://localhost");
  if (toUrl.pathname !== locUrl.pathname) return false;
  const toParams = new URLSearchParams(toUrl.search);
  const locParams = new URLSearchParams(locUrl.search);
  if (toParams.toString() !== locParams.toString()) return false;
  return true;
}

function NavPillLink({ to, children }) {
  const isActive = useIsActive(to);
  return (
    <NavLink
      to={to}
      className={`nav-pill ${isActive ? "nav-pill--active" : ""}`}
    >
      {children}
    </NavLink>
  );
}

function MobileNavLink({ to, onClick, children }) {
  const isActive = useIsActive(to);
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-6 py-2.5 text-sm font-medium rounded-xl mx-3 my-0.5 transition-all ${isActive ? "bg-[#4F46E5] text-white font-semibold shadow-md" : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-black dark:hover:text-white"}`}
    >
      {children}
    </Link>
  );
}

function ThemeToggle() {
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme } = useTheme();

  useEffect(() => { setMounted(true); }, []);

  if (!mounted) return <div className="w-9 h-9" />;

  return (
    <button
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      className="relative w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
    >
      <Sun
        size={18}
        weight="bold"
        className="absolute transition-all duration-300 text-amber-500 dark:rotate-0 dark:scale-0 dark:opacity-0 rotate-0 scale-100 opacity-100"
      />
      <Moon
        size={18}
        weight="bold"
        className="absolute transition-all duration-300 text-blue-400 rotate-90 scale-0 opacity-0 dark:rotate-0 dark:scale-100 dark:opacity-100"
      />
    </button>
  );
}

function CountrySwitcher() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("BD");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (code) => {
    if (code !== "BD") {
      toast.info("Coming soon", { description: `${COUNTRIES.find(c => c.code === code).name} is coming soon!` });
    }
    setOpen(false);
  };

  const current = COUNTRIES.find(c => c.code === selected);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="w-9 h-9 rounded-full flex items-center justify-center text-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
        aria-label="Switch country"
      >
        {current.flag}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg py-1 z-50 animate-in fade-in-0 zoom-in-95">
          {COUNTRIES.map(({ code, name, flag }) => (
            <button
              key={code}
              onClick={() => handleSelect(code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${code === selected ? "bg-neutral-50 dark:bg-neutral-800 font-semibold" : "hover:bg-neutral-50 dark:hover:bg-neutral-800"}`}
            >
              <span className="text-lg">{flag}</span>
              <span className="text-neutral-900 dark:text-neutral-100">{name}</span>
              {code === selected && (
                <span className="ml-auto text-[#4F46E5] text-xs font-bold">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileMenu({ open, onClose, user, onLogout }) {
  if (!open) return null;

  const close = () => onClose();

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999 }}>
      <div
        style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
        onClick={close}
      />
      <div
        className="dark:bg-neutral-900"
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          height: "100%",
          width: "300px",
          backgroundColor: "var(--bg)",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
          borderRadius: "1.5rem 0 0 1.5rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 20px 20px 24px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <img src={LOGO} alt="TicketUp" style={{ height: "32px", width: "auto" }} />
          <button
            onClick={close}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "8px", borderRadius: "10px", color: "var(--muted)" }}
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", paddingTop: "8px" }}>
          {!user && (
            <div style={{ padding: "8px 16px 4px" }}>
              <Link
                to="/login"
                onClick={close}
                className="dark:bg-neutral-800 dark:border-neutral-700 dark:text-[#6366F1]"
                style={{ display: "block", textAlign: "center", padding: "10px", borderRadius: "12px", fontSize: "14px", fontWeight: "600", color: "#4F46E5", backgroundColor: "#f0f0ff", textDecoration: "none", border: "1px solid #e0e0ff" }}
              >
                Sign in to your account
              </Link>
              <p style={{ textAlign: "center", marginTop: "12px", marginBottom: "4px", fontSize: "12px", color: "var(--muted)" }}>New here?</p>
            </div>
          )}

          <SectionLabel>Browse</SectionLabel>
          <MobileNavLink to="/events" onClick={close}>Events</MobileNavLink>
          <MobileNavLink to="/events?category=concerts" onClick={close}>Concerts</MobileNavLink>
          <MobileNavLink to="/events?category=sports" onClick={close}>Sports</MobileNavLink>
          <MobileNavLink to="/events?category=festivals" onClick={close}>Festivals</MobileNavLink>


          {user && (
            <>
              <div style={{ borderTop: "1px solid var(--border)", margin: "12px 16px" }} />
              <SectionLabel>Account</SectionLabel>
              <MobileNavLink to="/dashboard" onClick={close}>My Tickets</MobileNavLink>

              {(user.role === "organizer" || user.role === "admin") && (
                <>
                  <div style={{ borderTop: "1px solid var(--border)", margin: "12px 16px" }} />
                  <SectionLabel>Organizer</SectionLabel>
                  <MobileNavLink to="/organizer/scan" onClick={close}>Scan Tickets</MobileNavLink>
                  <MobileNavLink to="/organizer" onClick={close}>My Events</MobileNavLink>
                </>
              )}

              {user.role === "admin" && (
                <>
                  <div style={{ borderTop: "1px solid var(--border)", margin: "12px 16px" }} />
                  <SectionLabel>Admin</SectionLabel>
                  <MobileNavLink to="/admin" onClick={close}>Admin Panel</MobileNavLink>
                </>
              )}
            </>
          )}
        </div>

        {user && (
          <div className="dark:bg-neutral-950" style={{ flexShrink: 0, padding: "16px", borderTop: "1px solid var(--border)", backgroundColor: "var(--surface)" }}>
            <div className="dark:bg-neutral-800 dark:border-neutral-700" style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px", backgroundColor: "var(--bg)", borderRadius: "12px", border: "1px solid var(--border)", marginBottom: "10px" }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#4F46E5", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: "700", fontSize: "14px", flexShrink: 0 }}>
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontWeight: "600", fontSize: "13px", color: "var(--fg)", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</p>
                <p style={{ fontSize: "11px", color: "var(--muted)", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</p>
              </div>
            </div>
            <button
              onClick={() => { close(); onLogout(); }}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "10px", borderRadius: "12px", fontSize: "14px", fontWeight: "500", color: "#dc2626", backgroundColor: "transparent", border: "none", cursor: "pointer" }}
            >
              <SignOut size={18} /> Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  const adminActive = location.pathname === "/admin";
  const scanActive = location.pathname === "/organizer/scan";
  const organizerActive = location.pathname === "/organizer" && !scanActive;

  return (
    <>
      <header className="glass-header sticky top-0 z-50" data-testid="navbar">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <img src={LOGO} alt="TicketUp" className="h-11 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1 text-sm font-semibold">
            <NavPillLink to="/events">Events</NavPillLink>
            <NavPillLink to="/events?category=concerts">Concerts</NavPillLink>
            <NavPillLink to="/events?category=sports">Sports</NavPillLink>
            <NavPillLink to="/events?category=festivals">Festivals</NavPillLink>
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/events" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:text-black dark:hover:text-white" data-testid="search-link">
              <MagnifyingGlass size={18} /> Search
            </Link>

            <div className="flex items-center gap-1">
              <CountrySwitcher />
              <ThemeToggle />
            </div>

            {user ? (
              <div className="flex items-center gap-2">
                {user.role === "admin" && (
                  <Link to="/admin" className={`hidden sm:inline-flex items-center text-sm font-semibold ${adminActive ? "text-[#4F46E5]" : "text-neutral-700 dark:text-neutral-300 hover:text-black dark:hover:text-white"}`} data-testid="nav-admin">Admin</Link>
                )}
                {(user.role === "organizer" || user.role === "admin") && (
                  <>
                    <Link to="/organizer/scan" className={`hidden sm:inline-flex items-center text-sm font-semibold ${scanActive ? "text-[#4F46E5]" : "text-neutral-700 dark:text-neutral-300 hover:text-black dark:hover:text-white"}`} data-testid="nav-scan">Scan</Link>
                    <Link to="/organizer" className={`hidden sm:inline-flex items-center text-sm font-semibold ${organizerActive ? "text-[#4F46E5]" : "text-neutral-700 dark:text-neutral-300 hover:text-black dark:hover:text-white"}`} data-testid="nav-organizer">Organizer</Link>
                  </>
                )}
                <Link to="/dashboard" className="btn-outline text-sm !py-1.5 !px-3" data-testid="nav-dashboard">
                  <User size={14} className="inline mr-1" /> {user.name.split(" ")[0]}
                </Link>
                <button onClick={onLogout} className="text-neutral-500 dark:text-neutral-400 hover:text-black dark:hover:text-white" data-testid="logout-btn" aria-label="Logout">
                  <SignOut size={18} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="hidden lg:inline text-sm font-semibold hover:text-[#4F46E5] dark:text-neutral-300" data-testid="login-link">Sign in</Link>
                <Link to="/register" className="btn-red text-sm !py-1.5 !px-3" data-testid="register-link">Get Started</Link>
              </div>
            )}

            <button
              className="lg:hidden p-2 -mr-2 text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <List size={24} />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} user={user} onLogout={onLogout} />
    </>
  );
}
